import { ENVIRONMENTS, PLATFORMS, RESERVED_TAG_KEYS } from '../types';
const requiredStringErrors = (config) => {
    const requiredStrings = [
        'service',
        'version',
        'applicationId',
        'clientToken',
    ];
    return requiredStrings
        .filter(field => {
        const value = config[field];
        return typeof value !== 'string' || value.trim() === '';
    })
        .map(field => `"${field}" is required and must be a non-empty string`);
};
const optionalFieldErrors = (config) => {
    const errors = [];
    // Optional country. When provided, must be a non-empty string — passing an
    // empty string would emit a literal empty `country` tag to Datadog and break
    // any dashboard filtered on country.
    if (config.country !== undefined &&
        (typeof config.country !== 'string' || config.country.trim() === '')) {
        errors.push('"country" must be a non-empty string when provided. Omit the field for apps not tied to a single country.');
    }
    // Optional store identifier. Constraining to non-negative integers blocks
    // accidental strings, NaN, or floats that would otherwise reach Datadog as
    // arbitrary tag values and break dashboards filtered on `location_id`.
    const id = config.locationId;
    if (id !== undefined &&
        (typeof id !== 'number' || !Number.isInteger(id) || id < 0)) {
        errors.push(`"locationId" must be a non-negative integer when provided. Received: ${String(id)}`);
    }
    return errors;
};
const enumErrors = (config) => {
    const errors = [];
    if (!ENVIRONMENTS.includes(config.env)) {
        errors.push(`"env" must be one of: ${ENVIRONMENTS.join(', ')}. Received: "${String(config.env)}"`);
    }
    if (!PLATFORMS.includes(config.platform)) {
        errors.push(`"platform" must be one of: ${PLATFORMS.join(', ')}. Received: "${String(config.platform)}"`);
    }
    return errors;
};
// Sample rates are documented as percentages [0, 100]. Without this guard a
// NaN, negative, or out-of-range value reaches the Datadog SDK and surfaces
// as a runtime warning instead of a config-time failure.
const sampleRateErrors = (config) => {
    const optionalSampleRates = ['sessionSampleRate', 'sessionReplaySampleRate', 'logsSampleRate'];
    return optionalSampleRates
        .filter(field => {
        const value = config[field];
        return (value !== undefined &&
            (typeof value !== 'number' ||
                !Number.isFinite(value) ||
                value < 0 ||
                value > 100));
    })
        .map(field => `"${field}" must be a finite number between 0 and 100. Received: ${String(config[field])}`);
};
const additionalTagErrors = (config) => {
    if (!config.additionalTags)
        return [];
    const reservedConflicts = Object.keys(config.additionalTags).filter(key => RESERVED_TAG_KEYS.includes(key));
    if (reservedConflicts.length === 0)
        return [];
    return [
        `"additionalTags" cannot reuse reserved keys: ${reservedConflicts.join(', ')}. ` +
            `These tags are owned by the package and set from the validated config: ${RESERVED_TAG_KEYS.join(', ')}`,
    ];
};
export function validateConfig(config) {
    const errors = [
        ...requiredStringErrors(config),
        ...optionalFieldErrors(config),
        ...enumErrors(config),
        ...sampleRateErrors(config),
        ...additionalTagErrors(config),
    ];
    if (errors.length > 0) {
        throw new Error(`Datadog configuration validation failed:\n- ${errors.join('\n- ')}`);
    }
}
